import React, { useState } from 'react';
import { API_ENDPOINTS } from '../../../../utils/config';
import { useAuth } from '../../../../context/AuthContext';
import './AddRoleModal.scss';

interface AddRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (data?: any) => void;
  companyId: string;
}

interface RoleFormData {
  name: string;
  permissions: {
    open_rfq_request: boolean;
    edit_supplier: boolean;
    edit_product: boolean;
    approve_ability: boolean;
    see_all_requests: boolean;
    user_rights: boolean;
    edit_address: boolean;
    control_ability: boolean;
  };
}

const AddRoleModal: React.FC<AddRoleModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  companyId
}) => {
  const { token } = useAuth();
  const [formData, setFormData] = useState<RoleFormData>({
    name: '',
    permissions: {
      open_rfq_request: false,
      edit_supplier: false,
      edit_product: false,
      approve_ability: false,
      see_all_requests: false,
      user_rights: false,
      edit_address: false,
      control_ability: false
    }
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Role name is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/v2/companies/${companyId}/roles`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create role');
      }

      const result = await response.json();

      if (result.success) {
        // Reset form
        setFormData({
          name: '',
          permissions: {
            open_rfq_request: false,
            edit_supplier: false,
            edit_product: false,
            approve_ability: false,
            see_all_requests: false,
            user_rights: false,
            edit_address: false,
            control_ability: false
          }
        });

        if (onSuccess) {
          onSuccess(result.data);
        }

        onClose();
      } else {
        throw new Error(result.message || 'Failed to create role');
      }
    } catch (error) {
      console.error('Error creating role:', error);
      setError(error instanceof Error ? error.message : 'Failed to create role');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePermissionToggle = (permission: keyof RoleFormData['permissions']) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permission]: !prev.permissions[permission]
      }
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="admin-v2-modal-overlay" onClick={onClose}>
      <div className="admin-v2-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-v2-modal-header">
          <h2 className="admin-v2-modal-title">Add New Role</h2>
          <button
            className="admin-v2-modal-close-button"
            onClick={onClose}
            type="button"
          >
            ×
          </button>
        </div>

        <div className="admin-v2-modal-content">
          <form onSubmit={handleSubmit} className="add-role-form">
            {/* Role Name */}
            <div className="admin-v2-form-group">
              <label htmlFor="role-name" className="admin-v2-form-label">
                Role Name *
              </label>
              <input
                type="text"
                id="role-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="admin-v2-form-input"
                placeholder="Enter role name"
                required
              />
            </div>

            {/* Permissions Section */}
            <div className="admin-v2-form-group" style={{ marginTop: '1.5rem' }}>
              <label className="admin-v2-form-label">
                Permissions
              </label>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                padding: '1rem',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                {/* Open RFQ Request */}
                <div
                  onClick={() => handlePermissionToggle('open_rfq_request')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '4px',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span style={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color: '#000',
                    width: '24px',
                    textAlign: 'center',
                    display: 'inline-block',
                    padding: '2px 8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: '#fff'
                  }}>
                    {formData.permissions.open_rfq_request ? '✓' : '✗'}
                  </span>
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>
                    Open RFQ Request Permission
                  </span>
                </div>

                {/* Edit Supplier */}
                <div
                  onClick={() => handlePermissionToggle('edit_supplier')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '4px',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span style={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color: '#000',
                    width: '24px',
                    textAlign: 'center',
                    display: 'inline-block',
                    padding: '2px 8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: '#fff'
                  }}>
                    {formData.permissions.edit_supplier ? '✓' : '✗'}
                  </span>
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>
                    Edit Supplier Permission
                  </span>
                </div>

                {/* Edit Product */}
                <div
                  onClick={() => handlePermissionToggle('edit_product')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '4px',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span style={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color: '#000',
                    width: '24px',
                    textAlign: 'center',
                    display: 'inline-block',
                    padding: '2px 8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: '#fff'
                  }}>
                    {formData.permissions.edit_product ? '✓' : '✗'}
                  </span>
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>
                    Edit Product Permission
                  </span>
                </div>

                {/* Approve Ability */}
                <div
                  onClick={() => handlePermissionToggle('approve_ability')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '4px',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span style={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color: '#000',
                    width: '24px',
                    textAlign: 'center',
                    display: 'inline-block',
                    padding: '2px 8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: '#fff'
                  }}>
                    {formData.permissions.approve_ability ? '✓' : '✗'}
                  </span>
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>
                    Approve Ability Permission
                  </span>
                </div>

                {/* See All Requests */}
                <div
                  onClick={() => handlePermissionToggle('see_all_requests')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '4px',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span style={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color: '#000',
                    width: '24px',
                    textAlign: 'center',
                    display: 'inline-block',
                    padding: '2px 8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: '#fff'
                  }}>
                    {formData.permissions.see_all_requests ? '✓' : '✗'}
                  </span>
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>
                    See All Requests Permission
                  </span>
                </div>

                {/* User Rights */}
                <div
                  onClick={() => handlePermissionToggle('user_rights')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '4px',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span style={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color: '#000',
                    width: '24px',
                    textAlign: 'center',
                    display: 'inline-block',
                    padding: '2px 8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: '#fff'
                  }}>
                    {formData.permissions.user_rights ? '✓' : '✗'}
                  </span>
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>
                    User Rights Permission
                  </span>
                </div>

                {/* Edit Address */}
                <div
                  onClick={() => handlePermissionToggle('edit_address')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '4px',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span style={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color: '#000',
                    width: '24px',
                    textAlign: 'center',
                    display: 'inline-block',
                    padding: '2px 8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: '#fff'
                  }}>
                    {formData.permissions.edit_address ? '✓' : '✗'}
                  </span>
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>
                    Edit Address Permission
                  </span>
                </div>

                {/* Control Ability */}
                <div
                  onClick={() => handlePermissionToggle('control_ability')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '4px',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span style={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color: '#000',
                    width: '24px',
                    textAlign: 'center',
                    display: 'inline-block',
                    padding: '2px 8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: '#fff'
                  }}>
                    {formData.permissions.control_ability ? '✓' : '✗'}
                  </span>
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>
                    Control Ability Permission
                  </span>
                </div>
              </div>
            </div>

            {error && (
              <div style={{
                color: '#dc2626',
                fontSize: '14px',
                marginTop: '1rem',
                padding: '8px',
                backgroundColor: '#fef2f2',
                borderRadius: '4px',
                border: '1px solid #fecaca'
              }}>
                {error}
              </div>
            )}

            {/* Modal Actions */}
            <div className="admin-v2-modal-actions" style={{ marginTop: '1.5rem' }}>
              <button
                type="button"
                onClick={onClose}
                className="admin-v2-modal-cancel-button"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="admin-v2-modal-submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Create Role'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddRoleModal;





